"use client";

import { useEffect, useMemo, useState } from "react";
import { isRecipePulled } from "@/lib/commerce/recipe-pull";
import type { RecipeCoverage } from "@/lib/substitution/alternatives-api";

/**
 * Per-recipe rollup of live Shopify inventory availability (D2C store).
 *
 * Mirrors useLiveQuotes conventions: fires only for Shopify-sourced tenants,
 * dedupes plants across recipes, and fails open on transport/source-level
 * failure — any error or non-SHOPIFY response clears the map so the recipe UI
 * never blocks on an outage.
 *
 * Plants the server flagged `unmapped` (no Shopify variant resolved) count as
 * UNAVAILABLE: they cannot be purchased through the storefront, and the
 * quote-driven shopping list / cart already exclude them, so the recipe and
 * plant-selection screens must agree rather than let users pick plants that
 * later vanish from the shopping list.
 *
 * `recipePullOverrides` (PLA2-537) rides on the same `/api/commerce/
 * availability` response: recipe ids retailer staff manually kept live
 * despite low reported stock. An overridden recipe never reads as `soldOut`
 * here, keeping this client rollup and the server generation gate
 * (`checkGenerationStock`) in agreement. It does not affect per-plant
 * availability above.
 *
 * `coverage` (out-of-stock alternatives): when given, the server also reports,
 * per recipe, how many sold-out plants have an in-stock substitute, and the
 * pull rule counts those as available — a recipe stays selectable when the
 * swaps rescue it. The generation gate re-applies the same rule with the
 * substitutions the shopper actually picked.
 */

export interface RecipeAvailability {
  /** Every plant in the recipe, whether or not we have a stock signal for it. */
  total: number;
  /** Sold-out plants that have an in-stock substitute (counted as available). */
  healed: number;
  /** Plants the server returned an entry for (unmapped counts, as unavailable). */
  reported: number;
  /** Of the reported plants, how many are purchasable. Drives `soldOut`. */
  available: number;
  soldOut: boolean;
}

export interface RecipeForAvailability {
  id: string;
  genusAliases?: Array<{ id: string; shopifyVariantId?: string | null }>;
}

interface AvailabilityEntry {
  available: boolean;
  quantity: number | null;
  unmapped?: boolean;
}

interface AvailabilityResponse {
  source?: string;
  /** Tenant has an enabled Shopify connection (independent of stock fail-open). */
  selling?: boolean;
  availability?: Record<string, AvailabilityEntry>;
  /** PLA2-537: recipe ids manually kept live despite low reported stock. */
  recipePullOverrides?: string[];
  /** Out-of-stock alternatives: per recipe, sold-out plants with a substitute. */
  recipeCoverage?: Record<string, RecipeCoverage>;
}

export function useRecipeAvailability(
  recipes: RecipeForAvailability[],
  retailerId?: string | null,
  /**
   * Plants a fresher read (the generation stock gate) found unavailable. They
   * count as unavailable in the rollup so a recipe the gate pulled reads as
   * sold out here too, even if this hook's own read is older.
   */
  extraUnavailableIds?: ReadonlySet<string>,
  /** Catalog retailer (whose recipes these are) + customer zone, for coverage. */
  coverage?: { catalogRetailerId?: string | null; zone?: string | null }
): {
  recipeAvailability: Record<string, RecipeAvailability>;
  plantAvailability: Record<string, boolean>;
  /** The tenant's commerce resolved to a live Shopify store (it can sell). */
  selling: boolean;
  loading: boolean;
} {
  // plantId → boolean, ONLY for plants the server returned an entry for.
  const [plantAvailability, setPlantAvailability] = useState<
    Record<string, boolean>
  >({});
  const [selling, setSelling] = useState(false);
  const [loading, setLoading] = useState(false);
  // PLA2-537: recipe ids this tenant's staff manually kept live.
  const [overriddenRecipeIds, setOverriddenRecipeIds] = useState<Set<string>>(
    new Set()
  );
  const [recipeCoverage, setRecipeCoverage] = useState<
    Record<string, RecipeCoverage>
  >({});
  const catalogRetailerId = coverage?.catalogRetailerId ?? null;
  const zone = coverage?.zone ?? null;

  // Stable key: sorted set of recipe ids. Changing the set of recipes (not their
  // identity-unstable array wrapper) triggers a refetch; retailer change too.
  const recipeIdKey = useMemo(
    () => [...new Set(recipes.map((r) => r.id))].sort().join(","),
    [recipes]
  );

  useEffect(() => {
    if (!retailerId || recipes.length === 0) {
      setPlantAvailability({});
      setSelling(false);
      setOverriddenRecipeIds(new Set());
      setRecipeCoverage({});
      setLoading(false);
      return;
    }

    // Dedupe plants across all recipes by plantId; carry the raw variant id
    // (may be null — the server resolves via the SKU map / env override).
    const itemsById = new Map<
      string,
      { plantId: string; variantId: string | null }
    >();
    for (const recipe of recipes) {
      for (const alias of recipe.genusAliases ?? []) {
        if (!alias?.id || itemsById.has(alias.id)) continue;
        itemsById.set(alias.id, {
          plantId: alias.id,
          variantId: alias.shopifyVariantId ?? null,
        });
      }
    }
    const items = [...itemsById.values()];

    if (items.length === 0) {
      setPlantAvailability({});
      setSelling(false);
      setOverriddenRecipeIds(new Set());
      setRecipeCoverage({});
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const res = await fetch("/api/commerce/availability", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            retailerId,
            items,
            ...(catalogRetailerId
              ? { coverage: { catalogRetailerId, zone } }
              : {}),
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          setPlantAvailability({});
          setSelling(false);
          setOverriddenRecipeIds(new Set());
          setRecipeCoverage({});
          return;
        }

        const data = (await res.json()) as AvailabilityResponse;
        setSelling(data.selling ?? data.source === "SHOPIFY");
        setOverriddenRecipeIds(new Set(data.recipePullOverrides ?? []));
        setRecipeCoverage(data.recipeCoverage ?? {});
        if (data.source !== "SHOPIFY" || !data.availability) {
          setPlantAvailability({});
          return;
        }

        const next: Record<string, boolean> = {};
        for (const [plantId, entry] of Object.entries(data.availability)) {
          // Unmapped ⇒ not purchasable ⇒ unavailable, same as sold out.
          next[plantId] = entry?.available === true && entry?.unmapped !== true;
        }
        setPlantAvailability(next);
      } catch (err) {
        // Aborted requests are expected on unmount / key change — ignore.
        if ((err as Error)?.name === "AbortError") return;
        setPlantAvailability({});
        setSelling(false);
        setOverriddenRecipeIds(new Set());
        setRecipeCoverage({});
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeIdKey, retailerId, catalogRetailerId, zone]);

  // Roll up per-recipe availability. The server returns an entry for every
  // requested plant (unmapped included, as unavailable), so `reported`
  // normally equals `total`; plants absent from plantAvailability (e.g. the
  // whole response failed open) contribute to neither side of the ratio.
  const recipeAvailability = useMemo(() => {
    const out: Record<string, RecipeAvailability> = {};
    for (const recipe of recipes) {
      const aliases = recipe.genusAliases ?? [];
      const reportedFlags = aliases
        .filter(
          (alias) =>
            alias.id in plantAvailability || extraUnavailableIds?.has(alias.id)
        )
        .map(
          (alias) =>
            plantAvailability[alias.id] === true &&
            !extraUnavailableIds?.has(alias.id)
        );
      const reported = reportedFlags.length;
      const available = reportedFlags.filter(Boolean).length;
      // Never let substitutes cover more than what's actually missing (the
      // gate's fresher read may have marked extra plants unavailable).
      const healed = Math.min(
        recipeCoverage[recipe.id]?.healed ?? 0,
        reported - available
      );
      out[recipe.id] = {
        total: aliases.length,
        healed,
        reported,
        available,
        soldOut: isRecipePulled(
          available + healed,
          reported,
          overriddenRecipeIds.has(recipe.id)
        ),
      };
    }
    return out;
  }, [
    recipes,
    plantAvailability,
    extraUnavailableIds,
    overriddenRecipeIds,
    recipeCoverage,
  ]);

  return { recipeAvailability, plantAvailability, selling, loading };
}
